import { usePropertyContext } from '@/context/propertyContext';
import {
	type GroupPricingFragmentFragment,
	useDeleteGroupPricingMutation,
	useGetSavedGroupsQuery,
	useSaveGroupPricingMutation,
	useUpdateGroupPricingMutation,
} from '@/features/group-pricing/gql/_gen_/group-pricing.gql';
import type { FormSchema } from '@/features/group-pricing/zod';
import { useUser } from '@/features/users/context/userContext';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import type { z } from 'zod';
import { dateOnly } from '../date-utils';

// Convert GraphQL group data to form values
export const groupToFormValues = (
	group: GroupPricingFragmentFragment,
): z.infer<typeof FormSchema> => {
	return {
		adr_fcst_override: group.adr_fcst_override || 0,
		avg_stay_length: group.avg_stay_length || 0,
		// Just extract the date portion of the timestamp to avoid timezone conversion issues
		check_in: dateOnly(group.check_in),
		check_out: dateOnly(group.check_out),
		commission: group.commission || 0,
		comp_rooms: group.comp_rooms || 0,
		direct_var_opex: group.direct_var_opex || 0,
		f_and_b_profit: group.f_and_b_profit || 0,
		group_name: group.group_name || '',
		meeting_space_benefit: group.meeting_space_benefit || 0,
		occ_fcst_override: group.occ_fcst_override || 0,
		planners_adv_points: group.planners_adv_points || 0,
		profit_margin: group.profit_margin || 0,
		rate_requested: group.rate_requested || 0,
		rebate: group.rebate || 0,
		rooms_requested: group.rooms_requested || 0,
		wash_factor: group.wash_factor || 0,
	};
};

export const useGroupPricing = () => {
	const { user } = useUser();
	const { property } = usePropertyContext();
	const [activeGroupId, setActiveGroupId] = useState<string | null>(null);

	// Query to get saved groups
	const {
		data: groupsData,
		loading: groupsLoading,
		error: groupsError,
		refetch: refetchGroups,
	} = useGetSavedGroupsQuery({
		skip: !user || !property,
		variables: {
			propertyId: property?.propertyId,
			userId: user?.id,
		},
		fetchPolicy: 'network-only', // Don't use cache so we always get latest data
	});

	// Mutations for CRUD operations
	const [saveGroupPricing, { loading: saveLoading }] =
		useSaveGroupPricingMutation();
	const [updateGroupPricing, { loading: updateLoading }] =
		useUpdateGroupPricingMutation();
	const [deleteGroupPricing, { loading: deleteLoading }] =
		useDeleteGroupPricingMutation();

	// Array of saved groups
	const savedGroups = useMemo(() => {
		return groupsData?.getSavedGroups || [];
	}, [groupsData]);

	// Currently active group (if any)
	const activeGroup = useMemo(() => {
		if (!activeGroupId || !savedGroups) return null;
		return savedGroups.find((group) => group?.id === activeGroupId) || null;
	}, [activeGroupId, savedGroups]);

	// Default values for the form
	const defaultFormValues: z.infer<typeof FormSchema> = useMemo(() => {
		// Use active group values if available, otherwise use defaults
		if (activeGroup) {
			return groupToFormValues(activeGroup);
		}

		// Default values
		const today = new Date();
		const tomorrow = new Date();
		tomorrow.setDate(today.getDate() + 1);

		return {
			adr_fcst_override: 0,
			avg_stay_length: 1,
			check_in: format(today, 'yyyy-MM-dd'),
			check_out: format(tomorrow, 'yyyy-MM-dd'),
			commission: 10,
			comp_rooms: 0,
			direct_var_opex: 35,
			f_and_b_profit: 0,
			group_name: '',
			meeting_space_benefit: 0,
			occ_fcst_override: 0,
			planners_adv_points: 0,
			profit_margin: 10,
			rate_requested: 100, // Initial default of 100, will be updated by InputField when data is available
			rebate: 0,
			rooms_requested: 10,
			wash_factor: 10,
		};
	}, [activeGroup]);

	// Save a new group
	const saveGroup = async (formData: z.infer<typeof FormSchema>) => {
		if (!user || !property) {
			throw new Error('User or property information is missing');
		}

		try {
			const result = await saveGroupPricing({
				variables: {
					group: {
						...formData,
						property_id: property.propertyId,
						user_id: user.id,
						user_email: user.email,
					},
				},
			});

			await refetchGroups();

			// Set the new group as active
			const newGroupId = result.data?.saveGroupPricing.id;
			if (newGroupId) {
				setActiveGroupId(newGroupId);
			}

			return result.data?.saveGroupPricing;
		} catch (error) {
			console.error('Error saving group:', error);
			throw error;
		}
	};

	// Update an existing group
	const updateGroup = async (
		id: string,
		formData: z.infer<typeof FormSchema>,
	) => {
		if (!user || !property) {
			throw new Error('User or property information is missing');
		}

		try {
			const result = await updateGroupPricing({
				variables: {
					group: {
						id,
						...formData,
						property_id: property.propertyId,
						user_id: user.id,
						user_email: user.email,
					},
				},
			});

			await refetchGroups();
			return result.data?.updateGroupPricing;
		} catch (error) {
			console.error('Error updating group:', error);
			throw error;
		}
	};

	// Delete a group
	const deleteGroup = async (id: string) => {
		try {
			await deleteGroupPricing({
				variables: { id },
			});

			// If we deleted the active group, clear the active group
			if (activeGroupId === id) {
				setActiveGroupId(null);
			}

			await refetchGroups();
			return true;
		} catch (error) {
			console.error('Error deleting group:', error);
			throw error;
		}
	};

	// Set a specific group as active
	const setActiveGroup = (id: string | null) => {
		setActiveGroupId(id);
	};

	return {
		// Data
		savedGroups,
		activeGroup,
		defaultFormValues,

		// Loading states
		loading: groupsLoading || saveLoading || updateLoading || deleteLoading,

		// Error information
		error: groupsError,

		// Actions
		saveGroup,
		updateGroup,
		deleteGroup,
		setActiveGroup,
		refetchGroups,
	};
};
